import { CalendarClock, Stethoscope, UserRound } from 'lucide-react';
import {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import {
    useOrganizationCaregivers,
    useOrganizationClients,
    useShiftSearch,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const DEBOUNCE_MS = 200;
const MAX_RESULTS_PER_GROUP = 5;
// Shifts get their own cap: the date window below already bounds the result set,
// so this guards against a very common name on a dense schedule rather than
// acting as a feature limit.
const MAX_SHIFT_RESULTS = 25;
const SHIFT_WINDOW_DAYS_BACK = 7;
const SHIFT_WINDOW_DAYS_FORWARD = 14;

function formatShiftTime(isoString: string, timezone: string): string {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    });
}

function formatShiftDate(isoString: string, timezone: string): string {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    });
}

export type MentionDropdownHandle = {
    readonly moveSelection: (direction: 'up' | 'down') => void;
    readonly confirmSelection: () => void;
};

type MentionClient = {
    readonly first_name: string;
    readonly last_name: string;
    readonly preferred_name?: string | null;
};

/**
 * Every whitespace-separated query term must be a substring of the full or
 * preferred name, so word order never matters — mirrors the API's
 * caregiver search.
 */
export function clientMatchesQuery(
    client: MentionClient,
    query: string,
): boolean {
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const preferredName = (client.preferred_name ?? '').toLowerCase();
    return query
        .toLowerCase()
        .split(/\s+/)
        .filter((term) => term.length > 0)
        .every(
            (term) => fullName.includes(term) || preferredName.includes(term),
        );
}

export function MentionDropdown({
    open,
    query,
    organizationId,
    onSelect,
    onClose,
    dropdownRef,
}: {
    readonly open: boolean;
    readonly query: string;
    readonly organizationId: string | null;
    readonly onSelect: (type: string, id: string, displayName: string) => void;
    readonly onClose: () => void;
    readonly dropdownRef?: React.RefObject<MentionDropdownHandle | null>;
}) {
    const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
    const { timezone } = usePhoebeChatOrganizationScope();
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeValue, setActiveValue] = useState('');

    // Close on outside click
    useEffect(() => {
        if (!open) {
            return;
        }
        function handlePointerDown(event: PointerEvent) {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        }
        document.addEventListener('pointerdown', handlePointerDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [open, onClose]);

    // Only fetch while the dropdown is open — the composer renders this
    // component permanently, and these are org-wide list queries.
    const searchOrganizationId = open ? organizationId : null;

    const { data: caregivers } = useOrganizationCaregivers(
        searchOrganizationId,
        { reason: 'mention-search' },
        {
            search: debouncedQuery || undefined,
            per_page: MAX_RESULTS_PER_GROUP,
        },
    );

    const { data: allClients } = useOrganizationClients(searchOrganizationId, {
        reason: 'mention-search',
    });

    const filteredClients = useMemo(() => {
        if (!allClients) {
            return [];
        }
        if (!debouncedQuery) {
            return allClients.slice(0, MAX_RESULTS_PER_GROUP);
        }
        return allClients
            .filter((client) => clientMatchesQuery(client, debouncedQuery))
            .slice(0, MAX_RESULTS_PER_GROUP);
    }, [allClients, debouncedQuery]);

    // Scope shift suggestions to a window around today so the dropdown surfaces
    // the shifts a coordinator acts on, not the oldest matches in history.
    // Anchored to the chat user's local "today" (midnight boundaries); keyed on
    // `open` so it refreshes each time the dropdown opens and never goes stale
    // across midnight in a long-lived session.
    const shiftWindow = useMemo(() => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        start.setDate(start.getDate() - SHIFT_WINDOW_DAYS_BACK);
        end.setDate(end.getDate() + SHIFT_WINDOW_DAYS_FORWARD + 1);
        return {
            shift_start_date: start.toISOString(),
            shift_end_date: end.toISOString(),
        };
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- recompute the window only when the dropdown opens, not on every render
    }, [open]);

    const { data: shiftSearchResult, isValidating: isSearchingShifts } =
        useShiftSearch(
            searchOrganizationId,
            debouncedQuery.length >= 2 ? debouncedQuery : null,
            { reason: 'mention-search' },
            { limit: MAX_SHIFT_RESULTS, ...shiftWindow },
        );
    const shifts = useMemo(() => {
        if (debouncedQuery.length < 2) {
            return [];
        }
        // Exclude MISSING shifts: the source system no longer
        // recognizes these, so @mentioning one in chat would point the
        // agent at a dead shift id. Coordinators surface missing
        // shifts through the outreach flow (with a warning banner),
        // not through chat.
        return (shiftSearchResult?.shifts ?? []).filter(
            (shift) => shift.status !== 'missing',
        );
    }, [debouncedQuery, shiftSearchResult]);

    const hasCaregivers = caregivers !== undefined && caregivers.length > 0;
    const hasClients = filteredClients.length > 0;
    const hasShifts = shifts.length > 0;
    const hasResults = hasCaregivers || hasClients || hasShifts;
    const isSearching =
        caregivers === undefined ||
        allClients === undefined ||
        isSearchingShifts ||
        (debouncedQuery.length >= 2 && shiftSearchResult === undefined);

    // Close settled multi-word queries with no matches so an abandoned
    // mention ("@Jane thanks for covering") stops following the sentence.
    useEffect(() => {
        if (
            open &&
            !isSearching &&
            !hasResults &&
            debouncedQuery === query &&
            /\s/.test(debouncedQuery)
        ) {
            onClose();
        }
    }, [open, isSearching, hasResults, debouncedQuery, query, onClose]);

    // Build a lookup so we can resolve the active cmdk
    // value back to (type, id, displayName) on Enter.
    const itemLookup = useMemo(() => {
        const map = new Map<
            string,
            {
                readonly type: string;
                readonly id: string;
                readonly displayName: string;
            }
        >();
        for (const cg of caregivers ?? []) {
            map.set(`caregiver:${cg.id}`, {
                type: 'caregiver',
                id: cg.id,
                displayName: cg.name,
            });
        }
        for (const cl of filteredClients) {
            map.set(`client:${cl.id}`, {
                type: 'client',
                id: cl.id,
                displayName: `${cl.first_name} ${cl.last_name}`,
            });
        }
        for (const sh of shifts) {
            const shiftDisplay = sh.shift_start_time
                ? `${sh.patient_name}, ${formatShiftDate(sh.shift_start_time, timezone)}`
                : sh.patient_name;
            map.set(`shift:${sh.id}`, {
                type: 'shift',
                id: sh.id,
                displayName: shiftDisplay,
            });
        }
        return map;
    }, [caregivers, filteredClients, shifts, timezone]);

    // Auto-select the first item so Enter works immediately.
    // Preserve existing selection if it's still present so background
    // SWR revalidation does not clobber user arrow-key navigation.
    useEffect(() => {
        if (itemLookup.has(activeValue)) {
            return;
        }
        const firstKey = itemLookup.keys().next().value;
        if (firstKey) {
            setActiveValue(firstKey);
        }
    }, [itemLookup, activeValue]);

    const confirmSelection = useCallback(() => {
        const item = itemLookup.get(activeValue);
        if (item) {
            onSelect(item.type, item.id, item.displayName);
        }
    }, [activeValue, itemLookup, onSelect]);

    const moveSelection = useCallback(
        (direction: 'up' | 'down') => {
            const keys = [...itemLookup.keys()];
            if (keys.length === 0) {
                return;
            }
            const idx = keys.indexOf(activeValue);
            if (direction === 'down') {
                setActiveValue(keys[(idx + 1) % keys.length] ?? keys[0] ?? '');
            } else {
                setActiveValue(
                    keys[(idx - 1 + keys.length) % keys.length] ??
                        keys[keys.length - 1] ??
                        '',
                );
            }
        },
        [activeValue, itemLookup],
    );

    useImperativeHandle(dropdownRef, () => ({
        moveSelection,
        confirmSelection,
    }));

    if (!open) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-mention-dropdown=""
            // Keep the editor focused and its caret in place while
            // clicking items — a mid-click focus/caret move breaks the
            // click before cmdk's onClick can fire selection.
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            className="border-border bg-popover text-popover-foreground absolute bottom-full left-0 z-50 mb-1 w-80 overflow-hidden rounded-lg border shadow-lg"
        >
            <Command
                shouldFilter={false}
                value={activeValue}
                onValueChange={setActiveValue}
            >
                <CommandList>
                    {!hasResults ? (
                        <div className="text-muted-foreground px-3 py-2 text-sm">
                            {isSearching ? 'Searching…' : 'No matches'}
                        </div>
                    ) : null}
                    {hasCaregivers ? (
                        <CommandGroup heading="Caregivers">
                            {caregivers.map((cg) => (
                                <CommandItem
                                    key={cg.id}
                                    value={`caregiver:${cg.id}`}
                                    onSelect={() =>
                                        onSelect('caregiver', cg.id, cg.name)
                                    }
                                >
                                    <Stethoscope className="size-4 text-green-600" />
                                    <span className="truncate">{cg.name}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ) : null}
                    {hasClients ? (
                        <CommandGroup heading="Clients">
                            {filteredClients.map((cl) => (
                                <CommandItem
                                    key={cl.id}
                                    value={`client:${cl.id}`}
                                    onSelect={() =>
                                        onSelect(
                                            'client',
                                            cl.id,
                                            `${cl.first_name} ${cl.last_name}`,
                                        )
                                    }
                                >
                                    <UserRound className="size-4 text-purple-600" />
                                    <span className="truncate">
                                        {cl.first_name} {cl.last_name}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ) : null}
                    {hasShifts ? (
                        <CommandGroup heading="Shifts">
                            {shifts.map((sh) => (
                                <CommandItem
                                    key={sh.id}
                                    value={`shift:${sh.id}`}
                                    onSelect={() =>
                                        onSelect(
                                            'shift',
                                            sh.id,
                                            sh.shift_start_time
                                                ? `${sh.patient_name}, ${formatShiftDate(sh.shift_start_time, timezone)}`
                                                : sh.patient_name,
                                        )
                                    }
                                >
                                    <CalendarClock className="size-4 text-blue-600" />
                                    <span className="truncate">
                                        {sh.patient_name}
                                    </span>
                                    {sh.shift_start_time ? (
                                        <span className="text-muted-foreground ml-auto text-xs">
                                            {formatShiftTime(
                                                sh.shift_start_time,
                                                timezone,
                                            )}
                                        </span>
                                    ) : null}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ) : null}
                </CommandList>
            </Command>
        </div>
    );
}
